import { useEffect, useState } from "react";
// @mui
import {
  CircularProgress,
  IconButton,
  InputBase,
  InputBaseProps,
  Stack,
  Tooltip,
} from "@mui/material";
// utils
import Iconify from "../../../../components/iconify";
import useService from "../../../../hooks/useService";
// @types
import { IChatSendMessage } from "../../../../@types/chat";

// ----------------------------------------------------------------------

interface Props extends InputBaseProps {
  conversationId: string | null;
  clientId: string | null;
  onSend: (data: IChatSendMessage) => void;
  disabled?: boolean;
  /** Esconde o botão "Gerar mensagem com Alfred" — não faz sentido em
   * contextos onde não há um clientId real para a IA sugerir uma resposta
   * (ex.: chat de teste do Alfred Studio, onde quem digita é o próprio
   * dono simulando o cliente). */
  hideAiSuggestion?: boolean;
}

export default function ChatMessageInput({
  disabled,
  conversationId,
  clientId,
  onSend,
  hideAiSuggestion,
  sx,
  ...other
}: Props) {
  const [message, setMessage] = useState("");
  const [sendLoading, setSendLoading] = useState(false);

  const {
    post: triggerGenerateMessage,
    postData: generateMessageData,
    postLoading: generateMessageLoading,
  } = useService("/ai/message", {
    manual: true,
  });

  const handleSend = async () => {
    if (!message.trim() || !conversationId || disabled || sendLoading) return;
    setSendLoading(true);

    try {
      await onSend({
        conversationId,
        message: message.trim(),
      });
      setMessage("");
    } finally {
      setSendLoading(false);
    }
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    await handleSend();
  };

  const handleGenerateMessage = async () => {
    if (!clientId || disabled || generateMessageLoading) return;
    await triggerGenerateMessage({ clientId });
  };

  useEffect(() => {
    setMessage("");
  }, [conversationId, disabled]);


  useEffect(() => {
    if (generateMessageData?.message) {
      setMessage(generateMessageData.message);
    }
  }, [generateMessageData]);

  const isDisabled = disabled || generateMessageLoading || sendLoading;

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        pl: 2,
        pr: 1,
        height: 56,
        borderTop: (theme) => `solid 1px ${theme.palette.divider}`,
        ...sx,
      }}
    >
      <InputBase
        value={message}
        onKeyDown={handleKeyDown}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={disabled ? conversationId ? "Alfred está atendendo este chat" : "Nenhuma conversa selecionada" : "Escrever mensagem"}
        disabled={isDisabled}
        fullWidth
        {...other}
      />
      {!hideAiSuggestion && (
        <Tooltip
          title="Gerar mensagem com Alfred"
          placement="top"
          disableHoverListener={isDisabled}
          disableFocusListener={isDisabled}
          disableTouchListener={isDisabled}
        >
          <span>
            <IconButton disabled={isDisabled} color="primary" onClick={handleGenerateMessage}>
              {generateMessageLoading ? (
                <CircularProgress size={18} />
              ) : (
                <Iconify icon="ic:round-auto-awesome" width={20} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip
        title="Enviar mensagem"
        placement="top"
        disableHoverListener={isDisabled}
        disableFocusListener={isDisabled}
        disableTouchListener={isDisabled}
      >
        <span>
          <IconButton disabled={isDisabled} sx={{ml: 0}} color="inherit" onClick={handleSend}>
            {sendLoading ? (
              <CircularProgress size={18} />
            ) : (
              <Iconify icon="ic:round-send" width={20} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
